import * as firebase from "firebase/auth";
import { inMemoryPersistence, signInWithEmailAndPassword } from "firebase/auth";
import {
  PropsWithChildren,
  createContext,
  use,
  useEffect,
  useState,
} from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { auth, googleAuthProvider } from "../auth";
import {
  Session,
  SignInProvider,
  postSessionLogin,
  postSessionLogout,
  whoami,
} from "../http/onlyclicksApi";

interface AuthContextType {
  session: Session | undefined;
  isNotAsked: boolean;
  hasActiveSession: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  verifyPasswordResetCode: (code: string) => Promise<string>;
  confirmPasswordReset: (
    oobCode: string,
    email: string,
    newPassword: string,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<Session | undefined>(undefined);
  const [isNotAsked, setNotAsked] = useState(true);

  // TODO: handle errors and refactor duplicate code
  const signIn = async (email: string, password: string) => {
    await auth.setPersistence(inMemoryPersistence);
    const creds = await signInWithEmailAndPassword(auth, email, password);
    // Get the user's ID token as it is needed to exchange for a session cookie.
    const idToken = await creds.user.getIdToken();
    // Session login endpoint is queried and the session cookie is set.
    // CSRF protection should be taken into account.
    // const csrfToken = getCookie("csrfToken");
    await postSessionLogin({ id_token: idToken });
    setSession({
      id: creds.user.uid,
      email: creds.user.email!,
      providers: creds.user.providerData.map(
        (p) => p.providerId as SignInProvider,
      ),
    });
    setNotAsked(false);
    // The page redirect will be handled by the caller.
  };

  // TODO: handle errors and refactor duplicate code
  const signInWithPopup = async (provider: firebase.AuthProvider) => {
    const result = await firebase.signInWithPopup(auth, provider);
    // Get the user's ID token as it is needed to exchange for a session cookie.
    const idToken = await result.user.getIdToken();
    await postSessionLogin({ id_token: idToken });
    setSession({
      id: result.user.uid,
      email: result.user.email!,
      providers: result.user.providerData.map(
        (p) => p.providerId as SignInProvider,
      ),
    });
    setNotAsked(false);
  };

  // TODO: handle errors and refactor duplicate code
  // TODO: verify if we should create the user on the server side to avoid rate limiting or security issues
  const signUp = async (email: string, password: string) => {
    const creds = await firebase.createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    // Get the user's ID token as it is needed to exchange for a session cookie.
    const idToken = await creds.user.getIdToken();
    // Session login endpoint is queried and the session cookie is set.
    // CSRF protection should be taken into account.
    // const csrfToken = getCookie("csrfToken");
    await postSessionLogin({ id_token: idToken });
    setSession({
      id: creds.user.uid,
      email: creds.user.email!,
      providers: creds.user.providerData.map(
        (p) => p.providerId as SignInProvider,
      ),
    });
    setNotAsked(false);
    // The page redirect will be handled by the caller.
  };

  const signOut = async () => {
    await postSessionLogout();
    setSession(undefined); // TODO: Still required?
    setNotAsked(false); // TODO: Still required?
  };

  const sendPasswordResetEmail = (email: string) => {
    return firebase.sendPasswordResetEmail(auth, email);
  };

  const verifyPasswordResetCode = (code: string) => {
    return firebase.verifyPasswordResetCode(auth, code);
  };

  const confirmPasswordReset = (
    oobCode: string,
    email: string,
    newPassword: string,
  ) => {
    return firebase
      .confirmPasswordReset(auth, oobCode, newPassword)
      .then(() => signIn(email, newPassword));
  };

  useEffect(() => {
    whoami()
      .then((session) => setSession(session))
      .catch(() => setSession(undefined))
      .finally(() => setNotAsked(false));
  }, []);

  const value = {
    session,
    isNotAsked,
    hasActiveSession: session !== undefined,
    signIn,
    signInWithGoogle: () => signInWithPopup(googleAuthProvider),
    signUp,
    signOut,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
    confirmPasswordReset,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = () => {
  return use(AuthContext);
};

export const RequireAuth = () => {
  const auth = useAuth();
  const location = useLocation();
  if (!auth.hasActiveSession && !auth.isNotAsked) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they signin, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};
